import { useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Search, Loader2 } from "lucide-react";

const TABS = [
  { key: "semantic", label: "Semantico", testId: TID.memographTabSemantic, activeClass: "layer-semantic" },
  { key: "graph", label: "Grafo", testId: TID.memographTabGraph, activeClass: "layer-graph" },
  { key: "file", label: "File", testId: TID.memographTabFile, activeClass: "layer-file" },
];

export default function Memograph() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [tab, setTab] = useState("semantic");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/memograph/search", { query: q });
      setResults(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Errore");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const active = results?.[tab] || [];

  return (
    <section
      id="memograph"
      data-testid={TID.memographPanel}
      className="border border-[#1F1F1F] bg-[#0C0C0C]"
    >
      <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            MEMOGRAPH · memoria organizzativa
          </span>
        </div>
        <span className="font-mono text-[10px] text-neutral-600">3 livelli · semantico / grafo / file</span>
      </header>

      <form onSubmit={search} className="border-b border-[#1F1F1F] px-4 py-4 flex gap-2">
        <div className="flex-1 flex items-center gap-2 border border-[#1F1F1F] bg-[#050505] px-3 focus-within:border-[#EDEDED] transition-colors">
          <Search className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
          <input
            data-testid={TID.memographInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='> cerca un artefatto — es. "manifesto", "structura", "loop"'
            className="flex-1 bg-transparent outline-none py-2 font-mono text-sm placeholder:text-neutral-600 text-[#EDEDED]"
          />
        </div>
        <button
          data-testid={TID.memographSubmit}
          type="submit"
          disabled={loading || !query.trim()}
          className="border border-[#EDEDED] bg-[#EDEDED] text-black hover:bg-[#050505] hover:text-[#EDEDED] disabled:opacity-40 disabled:cursor-not-allowed px-4 font-mono text-xs uppercase tracking-widest transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Query"}
        </button>
      </form>

      <div className="border-b border-[#1F1F1F] px-4 flex">
        {TABS.map((t) => {
          const count = results?.[t.key]?.length ?? 0;
          const active_ = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={t.testId}
              onClick={() => setTab(t.key)}
              className={[
                "px-4 py-2 -mb-px border-b-2 font-mono text-[11px] uppercase tracking-[0.2em]",
                active_
                  ? t.activeClass
                  : "border-transparent text-neutral-500 hover:text-[#EDEDED]",
              ].join(" ")}
            >
              {t.label}
              <span className="ml-2 text-neutral-600">[{count}]</span>
            </button>
          );
        })}
      </div>

      <div className="min-h-[240px]">
        {err && (
          <div className="p-4 font-mono text-xs text-red-400">{err}</div>
        )}
        {!results && !err && (
          <div className="p-6 font-mono text-xs text-neutral-500">
            Nessuna query. Digita un termine e premi <span className="text-neutral-300">Query</span> per interrogare i 3 livelli.
          </div>
        )}
        {results && active.length === 0 && !err && (
          <div className="p-6 font-mono text-xs text-neutral-500">
            Nessun risultato in questo layer per <span className="text-neutral-300">&quot;{results.query}&quot;</span>. Prova un altro tab.
          </div>
        )}
        <ul className="divide-y divide-[#1F1F1F]">
          {active.map((r, i) => (
            <li
              key={`${r.node}-${i}`}
              data-testid={TID.memographResult}
              className="px-4 py-3 hover:bg-[#141414]"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0055FF] truncate">
                  node · {r.node}
                </span>
                <span className="font-mono text-[10px] text-neutral-500 shrink-0">
                  score {r.score.toFixed(2)}
                </span>
              </div>
              <div className="text-sm text-[#EDEDED] leading-snug">{r.excerpt}</div>
              <div className="font-mono text-[10px] text-neutral-500 mt-1 truncate">
                source · <span className="text-neutral-300">{r.source}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
